import { extractRequestFromQueryKey } from "../../../keys";
import { FORCE_INVALIDATE } from "../../../utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const ENDUSER_OBSERVATIONS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"enduser_observations"> =
  {
    acknowledge: (request, _, { session } = {}) => ({
      getOne: {
        predicate: ({ queryKey }) => {
          const queryRequest = extractRequestFromQueryKey(queryKey);

          return typeof queryRequest === "string"
            ? request.ids.includes(queryRequest)
            : typeof queryRequest?.id === "string"
            ? request.ids.includes(queryRequest.id)
            : false;
        },
        updater: (oldData) =>
          oldData
            ? request.excludeFromVitalCountLookback !== undefined
              ? {
                  ...oldData,
                  excludeFromVitalCountLookback: request.excludeFromVitalCountLookback,
                }
              : {
                  ...oldData,
                  reviewedAt: new Date(),
                  reviewedBy: session?.userInfo.id || "current-user",
                }
            : oldData,
      },
      getByIds: {
        predicate: ({ queryKey }) => {
          const queryRequest = extractRequestFromQueryKey(queryKey);

          return !!queryRequest?.ids?.some((id) => request.ids.includes(id));
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          if (request.excludeFromVitalCountLookback !== undefined) {
            return {
              ...oldData,
              matches: oldData.matches.map((observation) =>
                request.ids.includes(observation.id)
                  ? { ...observation, excludeFromVitalCountLookback: request.excludeFromVitalCountLookback }
                  : observation
              ),
            };
          }

          return {
            ...oldData,
            matches: oldData.matches.map((observation) =>
              request.ids.includes(observation.id)
                ? { ...observation, reviewedAt: new Date(), reviewedBy: session?.userInfo.id || "current-user" }
                : observation
            ),
          };
        },
      },
      getSome: {
        predicate: ({ queryKey, state: { data } }) => {
          if (data?.pages.some((page) => page.some((observation) => request.ids.includes(observation.id)))) {
            return true;
          }

          const queryRequest = extractRequestFromQueryKey(queryKey);

          if (queryRequest?.filter) {
            if ("reviewedBy" in queryRequest.filter || "reviewedAt" in queryRequest.filter) {
              return true;
            }
          }

          return false;
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          let updateCount = 0;
          if (request.excludeFromVitalCountLookback !== undefined) {
            const result = {
              ...oldData,
              pages: oldData.pages.map((page) =>
                page.map((observation) => {
                  if (!request.ids.includes(observation.id)) {
                    return observation;
                  }

                  updateCount++;
                  return {
                    ...observation,
                    excludeFromVitalCountLookback: request.excludeFromVitalCountLookback,
                  };
                })
              ),
            };

            if (updateCount !== request.ids.length) {
              return FORCE_INVALIDATE;
            }
            return result;
          }

          const result = {
            ...oldData,
            pages: oldData.pages.map((page) =>
              page.map((observation) => {
                if (!request.ids.includes(observation.id)) {
                  return observation;
                }

                updateCount++;
                return {
                  ...observation,
                  reviewedAt: new Date(),
                  reviewedBy: session?.userInfo.id || "current-user",
                };
              })
            ),
          };

          if (updateCount !== request.ids.length) {
            return FORCE_INVALIDATE;
          }
          return result;
        },
      },
    }),
  };
